import { isWebotServerError } from "../contacts/contactWriteErrors";
import type { CalendarEvent, REST_schema } from "../../@types/sharedSchema";

/**
 * Office tasks: a task one office member puts in a colleague's calendar.
 *
 * The task lives in the ASSIGNEE's calendar and is theirs; `assignedByUserId` names whoever put it
 * there. The browser never writes across calendars (firestore.rules) and never writes
 * `assignedByUserId` — every save that crosses calendars goes through
 * `/api/v1/calendar-tasks/*` (CalendarContext). Pure helpers, no I/O.
 * Server half: webot-server `docs/rules/calendar-task-assignment.md`.
 */

/** How far back "assigned by me" reaches — the same window as the "done" tab. */
export const ASSIGNED_TASKS_LOOKBACK_DAYS = 90;

export const normalizeUserEmail = (value?: string | null): string => (value ?? "").trim().toLowerCase();

/** Whose calendar a task is in: its stamped owner, or — for one not saved yet — the signed-in user. */
export const taskOwnerOf = (event: Pick<CalendarEvent, "userId"> | null | undefined, me: string): string =>
  normalizeUserEmail(event?.userId) || normalizeUserEmail(me);

/** A task I put in someone else's calendar. */
export const isAssignedByMe = (event: Pick<CalendarEvent, "userId" | "assignedByUserId">, me: string): boolean =>
  !!event.assignedByUserId &&
  normalizeUserEmail(event.assignedByUserId) === normalizeUserEmail(me) &&
  taskOwnerOf(event, me) !== normalizeUserEmail(me);

/** A task a colleague put in MY calendar. */
export const isAssignedToMe = (event: Pick<CalendarEvent, "userId" | "assignedByUserId">, me: string): boolean =>
  !!event.assignedByUserId && taskOwnerOf(event, me) === normalizeUserEmail(me);

/**
 * The task form's fields, as the server takes them. Only what the form edits — never `id`,
 * `userId`, `googleSync` or the assigner, which the server would ignore anyway.
 */
export const taskFieldsOf = (event: CalendarEvent): REST_schema.CalendarTaskFields => {
  const fields: REST_schema.CalendarTaskFields = {
    title: event.title,
    start: event.start,
    end: event.end,
    allDay: Boolean(event.allDay),
    status: event.status ?? "pending",
    attendees: event.attendees ?? [],
    listingIds: event.listingIds ?? [],
    listingsData: event.listingsData ?? []
  };
  if (typeof event.description === "string") fields.description = event.description;
  if (event.textColor) fields.textColor = event.textColor;
  if (event.backgroundColor) fields.backgroundColor = event.backgroundColor;
  return fields;
};

const KNOWN_REFUSALS = ["not_a_colleague", "not_your_task", "task_not_found", "user_not_found"] as const;

/** The translation key for a failed office-task request: the server's refusal when it named one. */
export const officeTaskErrorKey = (error: unknown): string => {
  if (isWebotServerError(error) && KNOWN_REFUSALS.includes(error.code as (typeof KNOWN_REFUSALS)[number])) {
    return `calendarPage.officeTasks.errors.${error.code}`;
  }
  return "calendarPage.form.taskOperationFailure";
};

/** "Assigned by me": open tasks first, soonest first; then done ones, latest first. */
export const sortAssignedTasks = (tasks: CalendarEvent[]): CalendarEvent[] => {
  const time = (event: CalendarEvent) => new Date(event.start).getTime();
  const open = tasks.filter((task) => task.status !== "done").sort((a, b) => time(a) - time(b));
  const done = tasks.filter((task) => task.status === "done").sort((a, b) => time(b) - time(a));
  return [...open, ...done];
};
